import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../components/Button";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import { useDialogFocus } from "../../hooks/useDialogFocus";
import { restoreGetStarted } from "../../services/onboarding";
import type { GetStartedTask } from "../../types/onboarding";
import { GetStartedItemList } from "./GetStartedPanel";
import {
  useGetStartedChecklist,
  type GetStartedChecklistStatus,
} from "./useGetStartedChecklist";
import "./GetStartedPanel.css";
import "./GetStartedDialog.css";

interface GetStartedDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The Get Started checklist, opened from the sidebar. The web copy of mobile's Profile > Help >
 * Get Started, and the permanent way back to a list the Players page panel stops showing once it
 * is hidden or finished.
 *
 * Unlike the panel it always lists the rows, done or not, hidden or not: a coach who opens it is
 * asking what the list says, and "nothing to show" would be the wrong answer to that.
 */
export const GetStartedDialog = ({ isOpen, onClose }: GetStartedDialogProps) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogFocus({ isOpen, dialogRef, onClose, lockScroll: true });

  if (!isOpen) return null;

  // Portalled, like GuidedToursModal: the sidebar that opens it is `position: sticky`, a stacking
  // context the overlay's z-index could not escape. No backdrop click, for the same reason that
  // dialog gives: a click handler on a static element is a finding the accessibility ratchet counts.
  return createPortal(
    <div className="get-started-dialog-overlay">
      <div
        ref={dialogRef}
        className="get-started-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid="get-started-dialog"
      >
        <GetStartedDialogBody titleId={titleId} onClose={onClose} />
      </div>
    </div>,
    document.body
  );
};

interface GetStartedDialogBodyProps {
  titleId: string;
  onClose: () => void;
}

/**
 * Split from the shell so the read starts when the dialog opens rather than whenever the sidebar
 * mounts, and so every open reads fresh state rather than what was true the last time it closed.
 */
const GetStartedDialogBody = ({ titleId, onClose }: GetStartedDialogBodyProps) => {
  // `includeItems`: this dialog is exactly the caller that renders the rows after a dismissal.
  // Without it the server short-circuits on the dismissal and sends no list at all.
  const { checklist, status, setChecklist, activeRef } = useGetStartedChecklist({
    includeItems: true,
  });
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(false);
  const [justRestored, setJustRestored] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // A successful restore removes the button holding focus. Focus moves to the heading rather than to
  // the status line, which is a live region and announces the change itself; focusing it as well
  // would read the same sentence twice. GetStartedVisibilityCard does the same.
  useEffect(() => {
    if (justRestored) headingRef.current?.focus();
  }, [justRestored]);

  const handleRestore = async () => {
    // aria-disabled leaves the handler attached, so this guard is what stops the second request.
    if (restoring) return;
    setRestoring(true);
    setRestoreError(false);
    try {
      // The service clears the cache and tells every mounted copy, so the panel on /players (which
      // may be the page behind this dialog) comes back without a reload.
      const updated = await restoreGetStarted();
      setChecklist(updated);
      if (!activeRef.current) return;
      if (updated?.dismissedAt) setRestoreError(true);
      else setJustRestored(true);
    } catch {
      if (activeRef.current) setRestoreError(true);
    } finally {
      if (activeRef.current) setRestoring(false);
    }
  };

  const isDismissed = Boolean(checklist?.dismissedAt);
  // Three states, the same as the settings card: absent `items` is "the server did not say", which
  // must not read as finished. Only a list we can see with nothing undone is finished.
  const items = checklist?.items;
  const finished = items ? !items.some((item) => !item.done) : false;
  // Restoring a finished list would bring back a panel that immediately hides itself again, so a
  // finished list offers nothing: the all-done line already says why the panel is gone. An unknown
  // (`items` absent) still offers it, because restoring is harmless and this is the way back.
  const canRestore = isDismissed && !finished;

  return (
    <>
      <div className="get-started-dialog__header">
        <h2 className="get-started-dialog__title" id={titleId} tabIndex={-1} ref={headingRef}>
          Get Started
        </h2>
        <ModalCloseButton onClick={onClose} />
      </div>
      <p className="get-started-dialog__intro">
        A few things that make the rest of RosterPilot useful. Each one ticks off once you
        have done it yourself.
      </p>

      <ChecklistContent status={status} items={items} onFollow={onClose} />

      {(canRestore || justRestored) && (
        <RestoreSection
          justRestored={justRestored}
          restoring={restoring}
          restoreError={restoreError}
          onRestore={handleRestore}
        />
      )}
    </>
  );
};

interface ChecklistContentProps {
  status: GetStartedChecklistStatus;
  items: GetStartedTask[] | undefined;
  onFollow: () => void;
}

/** The rows, or the one plain sentence that stands in for them. */
const ChecklistContent = ({ status, items, onFollow }: ChecklistContentProps) => {
  if (status === "loading") {
    return (
      <p className="get-started-dialog__message" role="status">
        Loading your checklist...
      </p>
    );
  }

  // An older API, a failed read, or an answer with no list in it. None of them means "done".
  if (status !== "ready" || !items) {
    return (
      <p className="get-started-dialog__message" data-testid="get-started-dialog-unavailable">
        We could not load your checklist right now. Try again in a moment.
      </p>
    );
  }

  // A real answer: the server sends only what this user can do, and there is nothing.
  if (items.length === 0) {
    return <p className="get-started-dialog__message">Nothing on the list needs you right now.</p>;
  }

  const allDone = !items.some((item) => !item.done);
  return (
    <>
      {allDone && (
        <p className="get-started-dialog__done" data-testid="get-started-dialog-all-done">
          {"You've done everything on the list."}
        </p>
      )}
      <GetStartedItemList items={items} onFollow={onFollow} />
    </>
  );
};

interface RestoreSectionProps {
  justRestored: boolean;
  restoring: boolean;
  restoreError: boolean;
  onRestore: () => void;
}

/** The "it's hidden, want it back?" line, and the way to bring it back. */
const RestoreSection = ({
  justRestored,
  restoring,
  restoreError,
  onRestore,
}: RestoreSectionProps) => (
  <div className="get-started-dialog__restore">
    <div>
      {/*
        A live region mounted before its text changes, which is what makes the "back" announcement
        reliable. It stays mounted after the restore because `justRestored` keeps this section up.
      */}
      <p
        className="get-started-dialog__status"
        role="status"
        data-testid="get-started-dialog-status"
      >
        {justRestored
          ? "The checklist is back on your Players page."
          : "You hid the checklist on your Players page."}
      </p>
      {restoreError && (
        <p className="get-started-panel__error" role="alert">
          We could not bring the checklist back just now. Try again in a moment.
        </p>
      )}
    </div>
    {/*
      `aria-disabled` rather than `disabled`, and not `loading`, for the reasons the settings card
      gives: a disabled button drops focus to <body> the moment it is pressed, and `loading` swaps the
      label for a generic "Loading...".
    */}
    {!justRestored && (
      <Button
        variant="secondary"
        onClick={onRestore}
        aria-disabled={restoring}
        aria-busy={restoring}
        data-testid="get-started-dialog-restore"
      >
        {restoring ? "Bringing it back…" : "Show on the Players page"}
      </Button>
    )}
  </div>
);
